export interface PlaybackRecoveryState {
  retrying: boolean;
  userPaused: boolean;
  autoplayBlocked: boolean;
}

export interface PlaybackRecoveryHooks {
  reconnect: () => void;
  healthy: () => void;
  state: (state: PlaybackRecoveryState) => void;
  diagnostic?: (event: string) => void;
}

/** Owns viewer intent separately from media events generated by load/detach. */
export class LivePlaybackRecovery {
  private active = true;
  private wanted = true;
  private expectedPause = false;
  private blocked = false;
  private retrying = false;
  private terminal = false;
  private generation = 0;
  private attempts = 0;
  private retryTimer: ReturnType<typeof setTimeout>;
  private watchdog: ReturnType<typeof setInterval>;
  private lastTime: number | null = null;
  private lastProgress = Date.now();
  private hasProgress = false;
  private healthySince = 0;
  private pendingPlay = false;
  private softRepairAttempted = false;
  private softRepairDeadline = 0;
  private diagnosticTimes = new Map<string, number>();
  private listeners: Array<() => void> = [];

  constructor(
    private video: HTMLVideoElement,
    private hooks: PlaybackRecoveryHooks,
    private page: Document = document,
    private network: Window = window
  ) {
    this.lastTime = video.readyState >= 2 && !video.seeking ? video.currentTime : null;
    this.listen(video, 'playing', () => {
      if (!this.wanted) { video.pause(); return; }
      this.expectedPause = false;
      if (this.lastTime === null && video.readyState >= 2 && !video.seeking) { this.lastTime = video.currentTime; }
      // A playing event can precede decoded frames, or repeat during stalls.
      // Only advancing media time may reset the watchdog/recovery state.
      this.emit();
    });
    this.listen(video, 'play', () => {
      if (video.paused || video.ended) { return; }
      if (!this.terminal) {
        this.wanted = true; this.blocked = false; this.emit();
      }
    });
    this.listen(video, 'pause', () => {
      // Consume the pause generated by our own reset, not every pause during
      // startup. It may arrive after replacement play() has already unpaused
      // the video; leaving the expectation set would swallow the next real pause.
      if (this.expectedPause) { this.expectedPause = false; this.diagnose('PAUSE_RESET'); return; }
      if (!video.paused) { this.diagnose('PAUSE_STALE'); return; }
      if (video.ended) { this.diagnose('PAUSE_MEDIA_ENDED'); }
      else if (video.error) { this.diagnose('PAUSE_MEDIA_ERROR'); }
      else if (this.hidden()) { this.diagnose('PAUSE_HIDDEN'); }
      else if (this.wanted) { this.diagnose('PAUSE_USER'); this.pause(); }
    });
    this.listen(video, 'canplay', () => this.play());
    this.listen(video, 'ended', () => this.repairOrRetry());
    this.listen(network, 'offline', () => {
      this.diagnose('OFFLINE');
      if (!this.wanted || this.terminal) { return; }
      this.softRepairDeadline = 0;
      this.retrying = true; this.cancelRetry(); this.emit();
    });
    this.listen(network, 'online', () => { this.diagnose('ONLINE'); this.wake(); });
    this.listen(page, 'visibilitychange', () => {
      this.diagnose(this.hidden() ? 'VISIBILITY_HIDDEN' : 'VISIBILITY_VISIBLE');
      if (!this.hidden()) { this.wake(); }
    });
    this.watchdog = setInterval(() => this.checkProgress(), 1000);
    this.emit();
  }

  get wantsPlayback(): boolean { return this.active && this.wanted && !this.terminal; }

  beginMediaChange(): void {
    if (!this.video.paused && !this.video.ended) { this.expectedPause = true; }
    this.generation++;
    this.pendingPlay = false;
    this.lastProgress = Date.now();
    this.lastTime = null;
    this.hasProgress = false;
    this.healthySince = 0;
    // Detach/reattach may be an in-place engine repair. Only actual progress
    // (or a new helper for an explicit selection) re-arms its one-shot budget.
    this.softRepairDeadline = 0;
  }

  play(): void {
    if (!this.wantsPlayback || this.pendingPlay || this.hidden() || this.video.error) { return; }
    const generation = this.generation;
    this.pendingPlay = true;
    const result = this.video.play();
    if (!result || typeof result.then !== 'function') { this.pendingPlay = false; return; }
    result.then(() => {
      if (!this.active || generation !== this.generation) { return; }
      this.pendingPlay = false;
      if (!this.wanted) { this.video.pause(); }
    }).catch((error) => {
      if (!this.active || generation !== this.generation) { return; }
      this.pendingPlay = false;
      if (error && error.name === 'NotAllowedError') {
        this.diagnose('PLAY_BLOCKED');
        this.wanted = false; this.blocked = true; this.retrying = false;
        this.resetSoftRepair();
        this.cancelRetry(); this.emit();
      } else if (!error || error.name !== 'AbortError') {
        this.retry();
      }
    });
  }

  pause(): void {
    this.generation++; this.pendingPlay = false;
    this.wanted = false; this.blocked = false; this.retrying = false;
    this.resetSoftRepair();
    this.cancelRetry(); this.video.pause(); this.emit();
  }

  resume(): void {
    if (!this.active || this.terminal) { return; }
    this.wanted = true; this.blocked = false;
    this.lastProgress = Date.now();
    this.emit();
    // Keep the user gesture in this call for native Safari's play permission.
    this.play();
    if (this.video.error || !this.video.getAttribute('src') && this.video.readyState === 0) { this.retry(0); }
  }

  retry(delayOverride?: number): void {
    if (!this.wantsPlayback) { return; }
    this.retrying = true;
    this.softRepairDeadline = 0;
    this.healthySince = 0;
    this.emit();
    if (this.retryTimer || this.hidden() || !this.online()) { return; }
    const delay = delayOverride === undefined ? Math.min(30000, 2000 * Math.pow(2, Math.min(this.attempts, 4))) : delayOverride;
    this.diagnose('RETRY_SCHEDULED');
    this.retryTimer = setTimeout(() => {
      this.retryTimer = undefined;
      if (!this.wantsPlayback || this.hidden() || !this.online()) { return; }
      // The next watchdog tick may not have observed a just-recovered player.
      // Preserve useful buffered playback instead of tearing it down now.
      if (this.observeProgress(Date.now())) { return; }
      this.attempts++;
      this.beginMediaChange();
      this.diagnose('RECONNECT');
      this.hooks.reconnect();
    }, delay);
  }

  stop(): void {
    this.terminal = true; this.wanted = false; this.retrying = false;
    this.resetSoftRepair();
    this.cancelRetry(); this.emit();
  }

  destroy(): void {
    this.active = false; this.generation++;
    this.resetSoftRepair();
    this.cancelRetry(); clearInterval(this.watchdog);
    this.listeners.forEach((remove) => remove()); this.listeners = [];
  }

  private checkProgress(): void {
    if (!this.wantsPlayback || this.hidden() || !this.online()) {
      this.lastProgress = Date.now();
      return;
    }
    const now = Date.now();
    // An internal repair's seek/play must make progress within one fixed grace
    // period. Repeated events, or a seek that never completes, cannot extend it.
    if (this.softRepairDeadline && now >= this.softRepairDeadline) {
      if (this.observeProgress(now)) { return; }
      this.diagnose('SOFT_REPAIR_EXHAUSTED');
      this.retry();
      return;
    }
    if (this.video.seeking) {
      this.lastProgress = now;
      if (!this.softRepairDeadline) { this.lastTime = this.video.currentTime; }
      return;
    }
    if (this.observeProgress(now)) { return; }
    if (now - this.lastProgress >= (this.hasProgress ? 15000 : 30000)) {
      this.healthySince = 0;
      if (!this.retrying) { this.diagnose(this.hasProgress ? 'WATCHDOG_STALL' : 'WATCHDOG_STARTUP'); }
      this.repairOrRetry();
    }
  }

  private observeProgress(now: number): boolean {
    if (!this.video.paused && !this.video.ended && !this.video.seeking && this.video.readyState >= 2 &&
      this.lastTime !== null && this.video.currentTime < this.lastTime - 0.1) {
      // Native live HLS may rebase its timeline without a seeking event. The
      // backward jump is a baseline only; subsequent forward frames are progress.
      this.lastTime = this.video.currentTime;
      this.healthySince = 0;
      this.diagnose('TIMELINE_REBASE');
      return false;
    }
    if (!this.video.paused && !this.video.ended && !this.video.seeking && this.video.readyState >= 2 && this.lastTime === null) {
      // A live playlist may start at a large timestamp. That first position
      // is a baseline, not proof that any frames have advanced.
      this.lastTime = this.video.currentTime;
      return false;
    }
    if (!this.video.paused && !this.video.ended && !this.video.seeking && this.video.readyState >= 2 &&
      this.video.currentTime - this.lastTime > 0.1) {
      this.lastTime = this.video.currentTime;
      this.lastProgress = now;
      this.hasProgress = true;
      this.resetSoftRepair();
      if (!this.healthySince) { this.healthySince = now; }
      this.retrying = false; this.cancelRetry(); this.emit();
      if (now - this.healthySince >= 15000) {
        this.attempts = 0; this.hooks.healthy();
      }
      return true;
    }
    return false;
  }

  private repairOrRetry(): void {
    if (!this.wantsPlayback || this.hidden() || !this.online() || this.retryTimer || this.softRepairDeadline) { return; }
    if (!this.softRepairAttempted && !this.video.error) {
      this.softRepairAttempted = true;
      const target = this.softRepairTarget();
      if (target !== undefined) {
        this.retrying = true;
        this.softRepairDeadline = Date.now() + 5000;
        this.healthySince = 0;
        if (target !== null) {
          try {
            this.video.currentTime = target;
            this.lastTime = target;
            this.diagnose('SOFT_REPAIR_SEEK_FORWARD');
          } catch (_) {
            this.diagnose('SOFT_REPAIR_UNAVAILABLE');
            this.retry();
            return;
          }
        } else { this.diagnose('SOFT_REPAIR_PLAY'); }
        this.emit();
        this.play();
        return;
      }
      this.diagnose('SOFT_REPAIR_UNAVAILABLE');
    }
    this.retry();
  }

  // null means useful data is already under the playhead; undefined means a
  // reload is needed. Never rewind into old media or jump to an unbuffered live
  // edge, which would discard the provider's LL-HLS hold-back.
  private softRepairTarget(): number | null | undefined {
    const position = this.video.currentTime;
    try {
      const buffered = this.video.buffered;
      for (let index = 0; buffered && index < buffered.length; index++) {
        const start = buffered.start(index);
        const end = buffered.end(index);
        if (start <= position + 0.05 && end > position + 0.25) { return null; }
        if (start > position && start - position <= 3 && end - start > 0.1) { return start + 0.05; }
      }
      const seekable = this.video.seekable;
      if (seekable && seekable.length) {
        const start = seekable.start(0);
        const end = seekable.end(0);
        if (position < start && end - start > 0.1) { return start + 0.05; }
      }
    } catch (_) { /* Live TimeRanges can change while being read. */ }
    return undefined;
  }

  private resetSoftRepair(): void { this.softRepairAttempted = false; this.softRepairDeadline = 0; }

  private diagnose(event: string): void {
    if (!this.active || !this.hooks.diagnostic) { return; }
    const now = Date.now();
    const previous = this.diagnosticTimes.get(event);
    if (previous !== undefined && now - previous < 1000) { return; }
    this.diagnosticTimes.set(event, now);
    try { this.hooks.diagnostic(event); } catch (_) { /* Diagnostics never affect playback. */ }
  }

  private wake(): void {
    if (!this.wantsPlayback || this.hidden() || !this.online()) { return; }
    if (this.retrying || Date.now() - this.lastProgress >= (this.hasProgress ? 15000 : 30000) || this.video.ended || this.video.error) {
      this.cancelRetry(); this.retry(0);
    } else if (this.video.paused) { this.play(); }
  }

  private online(): boolean { return this.network.navigator.onLine !== false; }
  private hidden(): boolean { return this.page.visibilityState === 'hidden'; }
  private cancelRetry(): void { clearTimeout(this.retryTimer); this.retryTimer = undefined; }
  private emit(): void {
    if (this.active) {
      this.hooks.state({
        retrying: this.retrying, userPaused: !this.wanted && !this.blocked && !this.terminal, autoplayBlocked: this.blocked
      });
    }
  }
  private listen(target: EventTarget, event: string, handler: () => void): void {
    target.addEventListener(event, handler);
    this.listeners.push(() => target.removeEventListener(event, handler));
  }
}
